// src/prototype/components/ea_reports/use_case/EAUseCaseArtifactShell.tsx
// ============================================================
// DVA EA USE CASE ARTIFACT — MAIN SHELL COMPONENT
// ============================================================
// Drop-in print shell for all use-case level EA deliverables.
//
// Usage:
//   <EAUseCaseArtifactShell
//     title="Board Rationale"
//     subtitle="Governance Decision Brief"
//     clientName="ACME Health System"
//     useCaseName="Care Coordination and Transition Management"
//     reportDate="March 2026"
//     summaryMetrics={metrics}
//     sections={sections}
//     manualPrint   ← add this to show the toolbar & suppress auto-print
//   />
//
// manualPrint prop (default: false):
//   false → legacy behavior: auto-fires window.print() after 800ms (no toolbar)
//   true  → shows sticky toolbar with Back + "Print / Save as PDF" button;
//           user controls when to print (consistent with Use Case Profile pattern)
// ============================================================

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { EAUseCaseArtifactHeader }  from './EAUseCaseArtifactHeader';
import { EAUseCaseSummaryStrip }    from './EAUseCaseSummaryStrip';
import { EAUseCaseSectionBlock }    from './EAUseCaseSectionBlock';
import { UA_PRINT_CSS }             from './EAUseCasePrintStyles';
import type { SummaryMetric }       from './EAUseCaseSummaryStrip';
import type { ArtifactSection }     from './EAUseCaseSectionBlock';

// ─────────────────────────────────────────────────────────────────────────────
// RE-EXPORTS — so consumers can import everything from the shell file
// ─────────────────────────────────────────────────────────────────────────────

export type { SummaryMetric } from './EAUseCaseSummaryStrip';
export type { ArtifactSection } from './EAUseCaseSectionBlock';
export { UACallout, UAMetricRow, UARiskRow } from './EAUseCaseSectionBlock';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export interface EAUseCaseArtifactShellProps {
  /** Primary artifact title, e.g. "Board Rationale" */
  title: string;
  /** Descriptor shown beneath the title, e.g. "Governance Decision Brief" */
  subtitle: string;
  /** Client / health system name, e.g. "ACME Health System" */
  clientName: string;
  /** The specific use case name this artifact covers */
  useCaseName: string;
  /** Display date string, e.g. "March 2026" */
  reportDate: string;
  /** Metrics shown in the summary strip beneath the header */
  summaryMetrics: SummaryMetric[];
  /** Ordered array of content sections to render */
  sections: ArtifactSection[];
  /**
   * When true: shows sticky toolbar with Back + Print button;
   * suppresses auto-print on load.
   * When false (default): auto-fires window.print() after 800ms.
   */
  manualPrint?: boolean;
  /**
   * Label shown in the toolbar's center breadcrumb when manualPrint=true.
   * Default: same as `title`
   */
  printLabel?: string;
  /**
   * Controls the browser's default filename when the user saves as PDF.
   * Default: "{title} - {useCaseName}"
   */
  printFilename?: string;
  /**
   * Optional override for the Early Adopter badge text.
   * Default: "Early Adopter Preview"
   */
  badgeLabel?: string;
  /**
   * Custom footer center text.
   * Default: "Adaptive Product · Data Value Accelerator"
   */
  footerCenter?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// WATERMARK
// ─────────────────────────────────────────────────────────────────────────────

const Watermark: React.FC = () => (
  <div className="ua-watermark" aria-hidden="true">
    Early Adopter
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// TOOLBAR (shown when manualPrint=true, hidden in print)
// ─────────────────────────────────────────────────────────────────────────────


/**
 * Artifacts open in a new tab, so there is no history to go "back" through and no router
 * state. The opening surface puts its own path in ?returnTo (see CFOPanel.appendReturnTo);
 * absent that — someone opening the print URL directly — /activate is the right landing.
 * Only same-origin absolute paths are honoured, so the parameter can't be used to bounce a
 * viewer somewhere else.
 */
function returnToFromUrl(): string {
  try {
    const v = new URLSearchParams(window.location.search).get('returnTo');
    if (v && v.startsWith('/') && !v.startsWith('//')) return v;
  } catch {
    /* no-op */
  }
  return '/activate';
}

const PrintToolbar: React.FC<{ label: string }> = ({ label }) => {
  const navigate = useNavigate();
  return (
    <div
      className="ua-no-print sticky top-0 z-50 bg-white border-b border-[#e5e7eb] px-6 py-3 flex items-center justify-between"
      style={{ fontFamily: 'Montserrat, sans-serif' }}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(returnToFromUrl())}
          className="flex items-center gap-2 text-sm text-[#717182] hover:text-[#18365E] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Execute
        </button>
        <div className="h-5 w-px bg-[#e5e7eb]" />
        <span className="text-sm font-semibold text-[#18365E]">
          {label} — Print Preview
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white text-sm font-semibold rounded-lg hover:bg-[#18365E]/90 transition-colors"
        >
          <Printer className="w-4 h-4" />
          Print / Save as PDF
        </button>
        <span className="text-xs text-[#717182]">
          Tip: In Print dialog, select "Save as PDF" for a digital copy
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────────────────

const Footer: React.FC<{ center?: string; useCaseName?: string }> = ({
  center,
  useCaseName,
}) => {
  const label = center ?? `Use Case Executive Summary - ${useCaseName ?? ''}`;
  return (
    <div className="ua-footer">
      <span className="ua-footer-brand">{label}</span>
      <span className="ua-footer-badge">Early Adopter Preview</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SHELL
// ─────────────────────────────────────────────────────────────────────────────

export const EAUseCaseArtifactShell: React.FC<EAUseCaseArtifactShellProps> = ({
  title,
  subtitle,
  clientName,
  useCaseName,
  reportDate,
  summaryMetrics,
  sections,
  manualPrint = false,
  printLabel,
  printFilename,
  badgeLabel,
  footerCenter,
}) => {
  // Inject global print + screen preview CSS once
  useEffect(() => {
    const styleId = 'ua-artifact-styles';
    if (!document.getElementById(styleId)) {
      const el = document.createElement('style');
      el.id = styleId;
      el.textContent = UA_PRINT_CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, []);

  // Set document title — controls the default filename in the browser's Save as PDF dialog
  useEffect(() => {
    const prev = document.title;
    document.title = printFilename ?? `${title} - ${useCaseName}`;
    return () => { document.title = prev; };
  }, [printFilename, title, useCaseName]);
  useEffect(() => {
    if (manualPrint) return;
    const isPrintRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.includes('/print');
    if (isPrintRoute) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, [manualPrint]);

  return (
    <>
      {/* Toolbar — only rendered when manualPrint=true, hidden in print via ua-no-print */}
      {manualPrint && <PrintToolbar label={printLabel ?? title} />}

      <div className="ua-page">
        {/* Watermark — fixed, appears on all pages */}
        <Watermark />

        {/* Header */}
        <EAUseCaseArtifactHeader
          title={title}
          subtitle={subtitle}
          clientName={clientName}
          useCaseName={useCaseName}
          reportDate={reportDate}
          badgeLabel={badgeLabel}
        />

        {/* Summary strip */}
        <EAUseCaseSummaryStrip metrics={summaryMetrics} />

        {/* Content sections */}
        {sections.map((section, i) => (
          <EAUseCaseSectionBlock
            key={i}
            index={i + 1}
            {...section}
          />
        ))}

        {/* Footer — fixed, appears on all pages (P35: per-document label from title) */}
        <Footer center={footerCenter ?? title} useCaseName={useCaseName} />
      </div>
    </>
  );
};

export default EAUseCaseArtifactShell;